<div class="request-edit">
  <div class="request-edit__content">
    <div class="request-edit__header">
      <div class="request-edit__heading">
        <h1 class="page-title">Edit Request</h1>
        <p class="page-subtitle">
          @if (isDraft) { Draft content can still be adjusted before confirmation reserves stock. } @else { This request is no longer
          editable. Review the current allocation and move it through operations instead. }
        </p>
      </div>
      <div class="request-edit__actions">
        <app-button type="button" variant="secondary" (click)="goBack()">Back</app-button>
        @if (isReady && isDraft) {
        <app-button type="button" [disabled]="isSaving || form.invalid || hasDuplicateMaterials" (click)="save()">
          {{ isSaving ? "Saving..." : "Save Draft" }}
        </app-button>
        }
      </div>
    </div>

    @if (isReady && request) { @if (!isDraft) {
    <div class="request-edit__status-banner">
      <ng-glyph name="info" size="20"></ng-glyph>
      <span>
        Request <strong>{{ request.requestNo }}</strong> is <strong>{{ request.status }}</strong>. Draft editing is closed, so this
        page is read-only.
      </span>
    </div>
    }

    <div class="request-edit__builder">
      <div class="request-edit__main">
        <app-card>
          <section class="request-edit__section">
            <div class="request-edit__section-heading">
              <span class="request-edit__section-kicker">1</span>
              <div class="request-edit__section-heading-main">
                <h2 class="request-edit__section-title">Request Setup</h2>
                <p class="request-edit__section-copy">
                  Keep the request owner, partner assignment, and fulfillment warehouse aligned.
                </p>
              </div>
            </div>

            <form [formGroup]="form" class="request-edit__form">
              <div class="request-edit__meta-grid">
                <div class="request-edit__form-field">
                  <label for="request-pages-edit-requesteditpage-field-1" class="request-edit__form-label">Request No</label>
                  <input type="text" class="request-edit__form-input" [value]="request.requestNo" disabled />
                </div>
                <div class="request-edit__form-field">
                  <label for="request-pages-edit-requesteditpage-field-2" class="request-edit__form-label">Status</label>
                  <input type="text" class="request-edit__form-input" [value]="request.status" disabled />
                </div>
              </div>

              <div class="request-edit__form-grid">
                <div class="request-edit__form-field">
                  <label for="request-pages-edit-requesteditpage-field-3" class="request-edit__form-label">Team Leader</label>
                  @if (isDraft) {
                  <app-autocomplete
                    [prefetchOnInit]="true"
                    [options]="teamLeaderOptions"
                    [selectedValue]="form.getRawValue().teamLeaderId || ''"
                    [minLength]="1"
                    [debounceMs]="300"
                    placeholder="Search team leader..."
                    (searchChange)="searchTeamLeaders($event)"
                    (selectionChange)="onTeamLeaderChange($event)"></app-autocomplete>
                  } @else {
                  <input type="text" class="request-edit__form-input" [value]="request.teamLeader?.name || '-'" disabled />
                  }
                </div>

                <div class="request-edit__form-field">
                  <label for="request-pages-edit-requesteditpage-field-4" class="request-edit__form-label">Partner</label>
                  @if (isDraft) {
                  <app-autocomplete
                    [prefetchOnInit]="true"
                    [disabled]="!canSelectPartner"
                    [options]="partnerOptions"
                    [selectedValue]="form.getRawValue().partnerId || ''"
                    [minLength]="0"
                    [debounceMs]="300"
                    placeholder="Search assigned partner..."
                    (searchChange)="searchPartners($event)"
                    (selectionChange)="onPartnerChange($event)"></app-autocomplete>
                  <span class="request-edit__helper-text">{{ partnerHelperText }}</span>
                  } @else {
                  <input type="text" class="request-edit__form-input" [value]="request.partner?.name || '-'" disabled />
                  }
                </div>
              </div>

              <div class="request-edit__form-grid request-edit__form-grid--single">
                <div class="request-edit__form-field">
                  <label for="request-pages-edit-requesteditpage-field-5" class="request-edit__form-label">Warehouse</label>
                  @if (isDraft) {
                  <app-autocomplete
                    [prefetchOnInit]="true"
                    [options]="warehouseOptions"
                    [selectedValue]="form.getRawValue().warehouseId || ''"
                    [minLength]="1"
                    [debounceMs]="300"
                    placeholder="Search warehouse..."
                    (searchChange)="searchWarehouses($event)"
                    (selectionChange)="onWarehouseChange($event)"></app-autocomplete>
                  } @else {
                  <input type="text" class="request-edit__form-input" [value]="request.warehouse?.name || '-'" disabled />
                  }
                </div>
              </div>

              <div class="request-edit__form-field">
                <label for="request-pages-edit-requesteditpage-field-6" class="request-edit__form-label">Note</label>
                <app-rich-text-editor formControlName="note" placeholder="Add request context..."></app-rich-text-editor>
              </div>
            </form>
          </section>
        </app-card>

        <app-card>
          <section class="request-edit__section">
            <div class="request-edit__section-heading">
              <span class="request-edit__section-kicker">2</span>
              <div class="request-edit__section-heading-main">
                <h2 class="request-edit__section-title">Request Items</h2>
                <p class="request-edit__section-copy">Each line shows the requested quantity and the current warehouse context.</p>
              </div>
              @if (isDraft) {
              <app-button type="button" variant="secondary" class="request-edit__section-heading-action" (click)="addItem()">
                <ng-glyph name="add" size="18"></ng-glyph>
                Add Line
              </app-button>
              }
            </div>

            <div [formGroup]="form">
              <div formArrayName="items" class="request-edit__items">
                @for (itemControl of items.controls; track $index) {
                <div [formGroup]="getItemForm($index)" class="request-edit__item-card">
                  <div class="request-edit__item-card-header">
                    <div>
                      <h3 class="request-edit__item-title">Line {{ $index + 1 }}</h3>
                      <p class="request-edit__item-copy">Requested {{ itemControl.get('qty')?.value ?? 0 }} units.</p>
                    </div>
                    @if (isDraft) {
                    <app-button type="button" variant="danger" [disabled]="items.length <= 1" (click)="removeItem($index)">
                      <ng-glyph name="delete" size="18"></ng-glyph>
                    </app-button>
                    }
                  </div>

                  <div class="request-edit__item-grid">
                    <div class="request-edit__item-field request-edit__item-field--material">
                      <label for="request-pages-edit-requesteditpage-field-7" class="request-edit__form-label">Material</label>
                      @if (isDraft) {
                      <app-autocomplete
                        [prefetchOnInit]="true"
                        [options]="materialOptionsByIndex[$index]"
                        [selectedValue]="itemControl.value.materialId ?? ''"
                        [minLength]="1"
                        [debounceMs]="300"
                        placeholder="Search material..."
                        (searchChange)="searchMaterials($event)"
                        (selectionChange)="onMaterialChange($index, $event)"></app-autocomplete>
                      @if (isDuplicateMaterial(getItemMaterialId($index), $index)) {
                      <span class="request-edit__error-text">This material is already used in another line.</span>
                      } } @else {
                      <input type="text" class="request-edit__form-input" [value]="getMaterialLabel($index)" disabled />
                      }
                    </div>

                    <div class="request-edit__item-field request-edit__item-field--qty">
                      <label for="request-pages-edit-requesteditpage-field-8" class="request-edit__form-label">Qty</label>
                      <input
                        type="number"
                        min="1"
                        appQtyInput
                        class="request-edit__form-input request-edit__qty-input"
                        formControlName="qty"
                        [readonly]="!isDraft"
                        (input)="onQtyInput()" />
                    </div>
                  </div>

                  <div class="request-edit__line-meta">
                    <span>Fulfilled: {{ request.items[$index]?.fulfilledQty ?? 0 }}</span>
                    <span>Remaining: {{ getRemainingQty($index) }}</span>
                  </div>

                  <div class="request-edit__stock-hint">
                    @if (!(form.get('warehouseId')?.value ?? '')) {
                    <span class="request-edit__stock-empty">Choose a warehouse to preview availability for this line.</span>
                    } @else if (!getItemMaterialId($index)) {
                    <span class="request-edit__stock-empty">Choose a material to preview stock for this line.</span>
                    } @else {
                    <div
                      class="request-edit__stock-card"
                      [class.request-edit__stock-card--ready]="getLineVisualState($index) === 'ready'"
                      [class.request-edit__stock-card--warning]="getLineVisualState($index) === 'warning'"
                      [class.request-edit__stock-card--unavailable]="getLineVisualState($index) === 'unavailable'"
                      [class.request-edit__stock-card--loading]="getLineVisualState($index) === 'loading'">
                      @if (getLineVisualState($index) === 'loading' && !getMaterialPreview($index)) {
                      <div class="request-edit__stock-loading">
                        <ng-glyph name="refresh" size="18" class="request-edit__spin"></ng-glyph>
                        Checking warehouse availability...
                      </div>
                      } @else {
                      <div
                        class="request-edit__stock-metrics"
                        [class.request-edit__stock-metrics--muted]="getLineVisualState($index) === 'unavailable'">
                        <div>
                          <span class="request-edit__stock-label">On Hand</span>
                          <strong>{{ getMaterialPreview($index)?.onHand ?? 0 }}</strong>
                        </div>
                        <div>
                          <span class="request-edit__stock-label">Reserved</span>
                          <strong>{{ getMaterialPreview($index)?.reserved ?? 0 }}</strong>
                        </div>
                        <div>
                          <span class="request-edit__stock-label">Available</span>
                          <strong>{{ getMaterialPreview($index)?.available ?? 0 }}</strong>
                        </div>
                      </div>
                      <p class="request-edit__stock-status">{{ getLineStatusMessage($index) }}</p>
                      }
                    </div>
                    }
                  </div>
                </div>
                }
              </div>
            </div>
          </section>
        </app-card>

        <app-card>
          <section class="request-edit__section">
            <div class="request-edit__section-heading">
              <span class="request-edit__section-kicker">3</span>
              <div class="request-edit__section-heading-main">
                <h2 class="request-edit__section-title">Availability Snapshot</h2>
                <p class="request-edit__section-copy">Review which lines are comfortably covered and which ones need attention.</p>
              </div>
            </div>

            @if (isFirstPreviewLoad()) {
            <div class="request-edit__snapshot-empty">
              <ng-glyph name="refresh" size="20" class="request-edit__spin"></ng-glyph>
              Refreshing warehouse stock preview...
            </div>
            } @else {
            <div class="request-edit__snapshot-list">
              @for (itemControl of items.controls; track $index) {
              <div class="request-edit__snapshot-row">
                <div class="request-edit__snapshot-copy">
                  <strong>Line {{ $index + 1 }}</strong>
                  <span class="request-edit__snapshot-detail">{{ getMaterialLabel($index) }}</span>
                </div>
                <span
                  class="request-edit__snapshot-pill"
                  [class.request-edit__snapshot-pill--ready]="getLineVisualState($index) === 'ready'"
                  [class.request-edit__snapshot-pill--warning]="getLineVisualState($index) === 'warning'"
                  [class.request-edit__snapshot-pill--loading]="getLineVisualState($index) === 'loading'"
                  [class.request-edit__snapshot-pill--unavailable]="getLineVisualState($index) === 'unavailable'">
                  {{ getLineStatusLabel($index) }}
                </span>
              </div>
              }
            </div>
            }
          </section>
        </app-card>
      </div>

      <div class="request-edit__aside">
        <app-card>
          <aside class="request-edit__review">
            <app-summary-panel
              title="Review"
              subtitle="Current request summary and draft-readiness snapshot."
              [rows]="reviewRows"></app-summary-panel>

            @if (hasDuplicateMaterials) {
            <div class="request-edit__review-alert">Remove duplicate materials before saving this request.</div>
            }
          </aside>
        </app-card>
      </div>
    </div>
    }
  </div>
</div>
